import { useState, useRef, useEffect } from "react";
import UtilsUnit from "../../../utils/UtilsUnit";
import { DropdownType } from "./WidgetDropdown";
import ConstantsImages from "../../../constants/ConstantsImages";
import ConstantsTexts from "../../../constants/ConstantsTexts";

export const useWidgetDropdown = ({
  options,
  onSelect,
  labelKey,
  selectedOptionProp,
  onClear,
  type = DropdownType.DEFAULT_SELECT,
  disabled = false,
  arrowImage = ConstantsImages.ARROW_IMAGE,
  enableDelete = false,
  usePortal = false,
}) => {
  // State variables for dropdown visibility, selected options, and search term
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(selectedOptionProp);
  const [selectedOptions, setSelectedOptions] = useState(selectedOptionProp ? [selectedOptionProp] : []); // For multi-select
  const [searchTerm, setSearchTerm] = useState("");

  // Portal-related state
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isPortalReady, setIsPortalReady] = useState(false);

  // Refs for dropdown element, list, and input
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Helper function to get disabled styles for text elements only
  const getDisabledTextStyles = () => ({
    opacity: disabled ? 0.5 : 1,
  });

  // Helper function to get disabled styles
  const getDisabledStyles = () => ({
    cursor: disabled ? "not-allowed" : "pointer",
    pointerEvents: disabled ? "none" : "auto",
  });

  // Helper function to get disabled click handler
  const getDisabledClickHandler = () => (disabled ? undefined : handleToggle);

  // Helper function to get arrow icon dimensions
  const getArrowIconDimensions = () => {
    switch (arrowImage) {
      case ConstantsImages.ARROW_IMAGE:
        return { width: "1.8rem", height: "1.8rem" };
      case ConstantsImages.ARROW_SMALL_IMAGE:
        return { width: "1.1rem", height: "0.7rem" };
      case ConstantsImages.ARROW_WHITE_IMAGE:
        return { width: "1.1rem", height: "0.6rem" };
      default:
        return { width: "1.8rem", height: "1.8rem" };
    }
  };

  // Helper function to get display text for multi-select
  const getMultiSelectDisplayText = () => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return "";
    }
    if (selectedOptions.length === 1) {
      return selectedOptions[0][labelKey];
    }
    return `${selectedOptions.length} ${ConstantsTexts.CODE_165}`;
  };

  // Helper function to check if option is selected (for multi-select)
  const isOptionSelected = (option) => {
    if (type === DropdownType.MULTI_SELECT) {
      return selectedOptions && selectedOptions.some((selected) => String(selected[labelKey]) === String(option[labelKey]));
    }
    return selectedOption && String(selectedOption[labelKey]) === String(option[labelKey]);
  };

  // Helper function to check if delete button should be shown for an option
  const shouldShowDeleteButton = (option) => {
    return (
      enableDelete &&
      type === DropdownType.DEFAULT_SELECT &&
      ((option.terminalVersionInt !== undefined && option.terminalVersionInt > 0) || (option.modelVersionInt !== undefined && option.modelVersionInt > 0))
    );
  };

  // Calculate position for portal-rendered dropdown
  const calculatePortalPosition = () => {
    if (usePortal && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const newPosition = {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      };
      setPortalPosition(newPosition);
      setIsPortalReady(true);
    }
  };

  // Handle option click based on dropdown type
  const handleOptionClick = (option) => {
    if (type === DropdownType.MULTI_SELECT) {
      handleMultiSelect(option);
    } else {
      handleSelect(option);
    }
  };

  // Toggle dropdown visibility
  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    setSearchTerm("");

    // Calculate portal position when opening
    if (newIsOpen && usePortal) {
      // Use setTimeout to ensure the dropdown is rendered before calculating position
      setTimeout(calculatePortalPosition, 0);
    } else if (!newIsOpen) {
      setIsPortalReady(false);
    }
  };

  // Handle item selection for DEFAULT_SELECT and SINGLE_SELECT
  const handleSelect = (option) => {
    if (type === DropdownType.MULTI_SELECT) {
      return; // Use handleMultiSelect for multi-select
    }

    setSelectedOption(option);

    // Close dropdown only for DEFAULT_SELECT
    if (type === DropdownType.DEFAULT_SELECT) {
      setIsOpen(false);
      setSearchTerm("");
      setIsPortalReady(false);
    }

    if (onSelect) {
      onSelect(option);
    }
  };

  // Handle multi-selection
  const handleMultiSelect = (option) => {
    if (type !== DropdownType.MULTI_SELECT) {
      return;
    }

    setSelectedOptions((prev) => {
      const optionKey = String(option[labelKey]);
      const isAlreadySelected = prev.some((selected) => String(selected[labelKey]) === optionKey);

      if (isAlreadySelected) {
        // Remove from selection
        const newSelection = prev.filter((selected) => String(selected[labelKey]) !== optionKey);

        if (onSelect) {
          onSelect(newSelection);
        }
        return newSelection;
      } else {
        // Add to selection
        const newSelection = [...prev, option];

        if (onSelect) {
          onSelect(newSelection);
        }
        return newSelection;
      }
    });
  };

  // Handle clear selection
  const handleClear = (e) => {
    e.stopPropagation();

    if (type === DropdownType.MULTI_SELECT) {
      setSelectedOptions([]);

      if (onSelect) {
        onSelect([]);
      }
    } else {
      setSelectedOption(null);

      if (onClear) {
        onClear(e);
      } else if (onSelect) {
        onSelect(null);
      }
    }
  };

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current) {
        // Check if click is outside the dropdown trigger
        const isOutsideTrigger = !dropdownRef.current.contains(event.target);

        // Check if click is outside portal content (if using portal)
        let isOutsidePortal = true;

        if (usePortal && typeof document !== "undefined") {
          const portalContent = document.querySelector('.dropdown-content-container[data-portal="true"]');

          if (portalContent && portalContent.contains(event.target)) {
            isOutsidePortal = false;
          }
        }

        // Close dropdown only if click is outside both trigger and portal content
        if (isOutsideTrigger && isOutsidePortal) {
          setIsOpen(false);
          setSearchTerm("");
          setIsPortalReady(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [usePortal]);

  // Reset selected option when the external prop changes
  useEffect(() => {
    if (type === DropdownType.MULTI_SELECT) {
      // For multi-select, selectedOptionProp should be an array
      setSelectedOptions(Array.isArray(selectedOptionProp) ? selectedOptionProp : []);
    } else {
      setSelectedOption(selectedOptionProp);
    }
  }, [selectedOptionProp, type]);

  // Filtered options based on search input
  const filteredOptions = options.filter((option) => {
    const value = option[labelKey];
    return !UtilsUnit.isValueUndefinedOrNull(value) && String(value).toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Scroll selected item into view at the top of the list only when dropdown opens
  useEffect(() => {
    if (isOpen && listRef.current) {
      if (type === DropdownType.MULTI_SELECT && selectedOptions && selectedOptions.length > 0) {
        // For multi-select, scroll to the first selected item in the list order (lowest index)
        let firstSelectedIndex = -1;

        for (let i = 0; i < filteredOptions.length; i++) {
          const option = filteredOptions[i];
          const isSelected = selectedOptions.some((selected) => String(selected[labelKey]) === String(option[labelKey]));

          if (isSelected) {
            firstSelectedIndex = i;
            break;
          }
        }
        if (firstSelectedIndex !== -1) {
          listRef.current.scrollToItem(firstSelectedIndex, "start");
        }
      } else if (selectedOption) {
        const selectedIndex = filteredOptions.findIndex((option) => String(option[labelKey]) === String(selectedOption[labelKey]));

        if (selectedIndex !== -1) {
          listRef.current.scrollToItem(selectedIndex, "start");
        }
      }
    }
  }, [isOpen]); // Only depend on isOpen to scroll only when dropdown opens/closes

  // Focus input when isOpen is true
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus(); // Focus the input element when the dropdown opens
    }
  }, [isOpen]); // Trigger this effect when isOpen changes

  // Close dropdown when disabled becomes true
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
      setSearchTerm("");
      setIsPortalReady(false);
    }
  }, [disabled, isOpen]);

  // Recalculate portal position on window resize
  useEffect(() => {
    if (usePortal) {
      const handleResize = () => {
        if (isOpen) {
          calculatePortalPosition();
        }
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [isOpen, usePortal]);

  return {
    isOpen,
    selectedOption,
    selectedOptions,
    searchTerm,
    setSearchTerm,
    inputRef,
    listRef,
    handleSelect,
    handleMultiSelect,
    handleClear,
    filteredOptions,
    dropdownRef,
    getDisabledTextStyles,
    getDisabledStyles,
    getDisabledClickHandler,
    getArrowIconDimensions,
    getMultiSelectDisplayText,
    isOptionSelected,
    handleOptionClick,
    shouldShowDeleteButton,
    portalPosition,
    isPortalReady,
    calculatePortalPosition,
  };
};
